import dayjs from "dayjs";
import _ from "lodash";
import firebase from "../../configs/FirebaseConfig";
import {
  FIRESTORE_PRODUCTS_TABLE,
  FIRESTORE_PRODUCT_PRICE_IMPORTS_TABLE,
} from "../../constants/FirestoreConstant";
import { pricesAreEqual, toNumber } from "../priceImport";

const firestore = firebase.firestore();

/**
 * Schrijfkant van de bulk-prijsimport.
 *
 * Uitgangspunten (het gaat om verkoopprijzen, dus stabiliteit boven snelheid):
 * 1. Vóór het schrijven leggen we de volledige run vast met álle oude prijzen. Dat document is de
 *    bron voor het terugdraaien, ook als de browser er halverwege uitklapt.
 * 2. Schrijven gebeurt in transacties van maximaal PRICE_IMPORT_CHUNK_SIZE producten. In de
 *    transactie wordt het product opnieuw gelezen en vergeleken met de prijs die de gebruiker in
 *    het controlescherm zag. Wijkt die af, dan slaan we de regel over en rapporteren we dat -
 *    zo overschrijven we nooit stilletjes een wijziging van iemand anders.
 * 3. Alles is idempotent: staat de nieuwe prijs er al, dan telt de regel als doorgevoerd. Een
 *    tweede poging na een storing kan dus geen dubbele schade doen.
 */

/** Firestore staat maximaal 500 bewerkingen per transactie toe; 100 producten = 200 bewerkingen. */
export const PRICE_IMPORT_CHUNK_SIZE = 100;

/** Aantal pogingen per blok bij netwerk-/contentiefouten. */
const CHUNK_ATTEMPTS = 3;

export const PRICE_IMPORT_RUN_STATUS = {
  PENDING: "pending",
  COMPLETED: "completed",
  COMPLETED_WITH_ISSUES: "completed_with_issues",
  FAILED: "failed",
  ROLLED_BACK: "rolled_back",
  ROLLBACK_WITH_ISSUES: "rollback_with_issues",
};

/** Alleen de velden die we nodig hebben voor rapportage en terugdraaien. */
const toRunLine = (line) => ({
  productId: line.productId,
  code: line.productCode || line.fileCode || null,
  name: line.productName || line.fileName || null,
  oldPrice: toNumber(line.oldPrice),
  newPrice: toNumber(line.newPrice),
});

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Legt de run vast vóórdat er iets aan producten verandert.
 * @returns {Promise<{id: string|null, error: Error|null}>} id is null als het loggen niet mocht/kon.
 */
export const createPriceImportRun = async ({ lines, email, fileName, summary }) => {
  const runLines = lines.map(toRunLine);
  const payload = {
    createdAt: dayjs().unix(),
    createdBy: email || null,
    fileName: fileName || null,
    status: PRICE_IMPORT_RUN_STATUS.PENDING,
    lineCount: runLines.length,
    summary: summary || null,
    lines: runLines,
    appliedCount: 0,
    conflictCount: 0,
    missingCount: 0,
  };

  try {
    const doc = await firestore.collection(FIRESTORE_PRODUCT_PRICE_IMPORTS_TABLE).add(payload);
    return { id: doc.id, error: null };
  } catch (error) {
    console.error("Kon prijsimport-run niet vastleggen:", error);
    // Tweede poging zonder de regels: bij een te groot document blijft de run zelf wel bruikbaar
    // als historie. Het terugdraaien gaat dan via het gedownloade Excel-bestand.
    try {
      const doc = await firestore
        .collection(FIRESTORE_PRODUCT_PRICE_IMPORTS_TABLE)
        .add({ ..._.omit(payload, ["lines"]), linesStored: false });
      return { id: doc.id, error: null };
    } catch (secondError) {
      console.error("Kon prijsimport-run ook zonder regels niet vastleggen:", secondError);
      return { id: null, error: secondError };
    }
  }
};

/** Best-effort bijwerken van de run; mag de prijsupdate zelf nooit blokkeren. */
export const updatePriceImportRun = async (runId, data) => {
  if (!runId) return;
  try {
    await firestore.collection(FIRESTORE_PRODUCT_PRICE_IMPORTS_TABLE).doc(runId).update(data);
  } catch (error) {
    console.error("Kon prijsimport-run niet bijwerken:", error);
  }
};

/**
 * Voert één blok door in een transactie.
 * @returns {Promise<{applied: Array, conflicts: Array, missing: Array}>}
 */
const applyChunk = async (chunk, { email, expectedField = "oldPrice", targetField = "newPrice" }) => {
  let result = { applied: [], conflicts: [], missing: [] };

  await firestore.runTransaction(async (transaction) => {
    // Belangrijk: in Firestore-transacties moeten álle reads vóór de writes gebeuren.
    const refs = chunk.map((line) => firestore.collection(FIRESTORE_PRODUCTS_TABLE).doc(line.productId));
    const snapshots = await Promise.all(refs.map((ref) => transaction.get(ref)));

    // Bij een retry van de transactie beginnen we met een schone lijst.
    const attempt = { applied: [], conflicts: [], missing: [] };
    const timestamp = dayjs().unix();

    snapshots.forEach((snapshot, index) => {
      const line = chunk[index];
      const expectedPrice = toNumber(line[expectedField]);
      const targetPrice = toNumber(line[targetField]);

      if (!snapshot.exists) {
        attempt.missing.push({ ...line });
        return;
      }

      const currentPrice = toNumber(snapshot.data()?.price);

      if (pricesAreEqual(currentPrice, targetPrice)) {
        // Al doorgevoerd (bv. bij een tweede poging): geen write nodig, wel als doorgevoerd tellen.
        attempt.applied.push({ ...line });
        return;
      }

      if (!pricesAreEqual(currentPrice, expectedPrice)) {
        // Iemand anders heeft de prijs tussentijds gewijzigd. Niet overschrijven.
        attempt.conflicts.push({ ...line, currentPrice });
        return;
      }

      transaction.update(refs[index], {
        price: targetPrice,
        updatedAt: timestamp,
        updatedBy: email || null,
      });
      attempt.applied.push({ ...line });
    });

    result = attempt;
  });

  return result;
};

const applyInChunks = async (lines, { email, expectedField, targetField, onProgress }) => {
  const chunks = _.chunk(lines, PRICE_IMPORT_CHUNK_SIZE);
  const applied = [];
  const conflicts = [];
  const missing = [];
  let processed = 0;

  for (let index = 0; index < chunks.length; index++) {
    const chunk = chunks[index];
    let lastError = null;
    let chunkResult = null;

    for (let attempt = 1; attempt <= CHUNK_ATTEMPTS; attempt++) {
      try {
        chunkResult = await applyChunk(chunk, { email, expectedField, targetField });
        lastError = null;
        break;
      } catch (error) {
        lastError = error;
        console.error(`Prijsblok ${index + 1} mislukt (poging ${attempt}):`, error);
        if (attempt < CHUNK_ATTEMPTS) await sleep(500 * attempt);
      }
    }

    if (lastError) {
      const failure = new Error(lastError.message || "Prijsupdate mislukt");
      failure.code = lastError.code;
      failure.partial = { applied, conflicts, missing, processed };
      throw failure;
    }

    applied.push(...chunkResult.applied);
    conflicts.push(...chunkResult.conflicts);
    missing.push(...chunkResult.missing);
    processed += chunk.length;

    if (onProgress) onProgress({ processed, total: lines.length });
  }

  return { applied, conflicts, missing, processed };
};

/**
 * Voert de geselecteerde prijswijzigingen door.
 * @returns {Promise<{applied: Array, conflicts: Array, missing: Array}>}
 * @throws {Error} met `partial` als een blok definitief mislukt
 */
export const applyPriceImportLines = async ({ lines, email, runId, onProgress }) => {
  const result = await applyInChunks(lines, {
    email,
    expectedField: "oldPrice",
    targetField: "newPrice",
    onProgress: async (progress) => {
      if (onProgress) onProgress(progress);
    },
  });

  await updatePriceImportRun(runId, {
    status:
      result.conflicts.length > 0 || result.missing.length > 0
        ? PRICE_IMPORT_RUN_STATUS.COMPLETED_WITH_ISSUES
        : PRICE_IMPORT_RUN_STATUS.COMPLETED,
    appliedCount: result.applied.length,
    conflictCount: result.conflicts.length,
    missingCount: result.missing.length,
    conflicts: result.conflicts.map((line) => ({ ...toRunLine(line), currentPrice: toNumber(line.currentPrice) })),
    missing: result.missing.map(toRunLine),
    finishedAt: dayjs().unix(),
  });

  return result;
};

/** Markeert een run als mislukt, met de regels die wél zijn doorgevoerd. */
export const markPriceImportRunFailed = async (runId, error) => {
  await updatePriceImportRun(runId, {
    status: PRICE_IMPORT_RUN_STATUS.FAILED,
    appliedCount: error?.partial?.applied?.length || 0,
    conflictCount: error?.partial?.conflicts?.length || 0,
    missingCount: error?.partial?.missing?.length || 0,
    error: error?.message || "Onbekende fout",
    finishedAt: dayjs().unix(),
  });
};

/**
 * Draait een doorgevoerde run terug: zet de oude prijs terug, maar alleen waar de huidige prijs
 * nog gelijk is aan de prijs die deze import heeft gezet.
 */
export const rollbackPriceImportRun = async ({ run, email, onProgress }) => {
  const appliedLines = (run?.lines || []).filter((line) => !!line.productId);
  if (appliedLines.length === 0) {
    return { applied: [], conflicts: [], missing: [], processed: 0 };
  }

  // Bij terugdraaien is de verwachte huidige prijs de nieuwe prijs, en het doel de oude prijs.
  const result = await applyInChunks(appliedLines, {
    email,
    expectedField: "newPrice",
    targetField: "oldPrice",
    onProgress,
  });

  await updatePriceImportRun(run.id, {
    status:
      result.conflicts.length > 0 || result.missing.length > 0
        ? PRICE_IMPORT_RUN_STATUS.ROLLBACK_WITH_ISSUES
        : PRICE_IMPORT_RUN_STATUS.ROLLED_BACK,
    rolledBackAt: dayjs().unix(),
    rolledBackBy: email || null,
    rollbackAppliedCount: result.applied.length,
    rollbackConflictCount: result.conflicts.length,
    rollbackMissingCount: result.missing.length,
  });

  return result;
};

/** Laatste imports voor het historiek-overzicht. */
export const fetchPriceImportRuns = async (limit = 10) => {
  const snapshot = await firestore
    .collection(FIRESTORE_PRODUCT_PRICE_IMPORTS_TABLE)
    .orderBy("createdAt", "desc")
    .limit(limit)
    .get();

  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
};
